import { LINKEDIN_AUTOMATION_STAGE_STATUSES } from './linkedinAutomationStageStatuses'
import * as linkedinUrls from './linkedinUrls'
import { LinkedinSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { LinkedinRecommendationKey, StageType } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import {
  getCurrentValueForCheckbox,
  saveCurrentValueForCheckbox,
  toggleCheckbox,
} from '../checkboxDetection'
import {
  defaultCantUndoFn,
  getErrorIsDisabledAndReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => (value ? 'On' : 'Off'))

const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => !value)

const getCantUndoFn = () => defaultCantUndoFn

function getIsDisabledFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabled
}
function getIsDisabledReasonFn(settingsKey: string) {
  const fn = getErrorIsDisabledAndReasonFn(settingsKey)
  return (automationErrors) => fn(automationErrors).isDisabledReason
}

const settingsLabels: Partial<Record<LinkedinSettingKey, string>> = {
  [LinkedinSettingKey.JOB_SEARCH]: 'Allow job search notifications',
  [LinkedinSettingKey.HIRING_SOMEONE]: 'Allow job post notifications',
  [LinkedinSettingKey.CONNECTING_WITH_OTHERS]:
    'Allow connections related notifications',
  [LinkedinSettingKey.POSTING_COMMENTING]: 'Allow post related notifications',
  [LinkedinSettingKey.MESSAGING]: 'Allow messaging notifications',
  [LinkedinSettingKey.GROUPS]: 'Allow group notifications',
  [LinkedinSettingKey.PAGES]: 'Allow Page notifications',
  [LinkedinSettingKey.EVENTS]: 'Allow event notifications',
  [LinkedinSettingKey.NEWS]: 'Allow editorial notifications',
  [LinkedinSettingKey.REPORTS]: 'Allow reports and insights notifications',
  [LinkedinSettingKey.UPDATING_YOUR_PROFILE]:
    'Allow profile improvement notifications',
  [LinkedinSettingKey.VERIFICATIONS]: 'Allow verification notifications',
}

const settingsUrls: Partial<Record<LinkedinSettingKey, string>> = {
  [LinkedinSettingKey.JOB_SEARCH]: linkedinUrls.jobSearchNotificationUrl,
  [LinkedinSettingKey.HIRING_SOMEONE]: linkedinUrls.hiringNotificationUrl,
  [LinkedinSettingKey.CONNECTING_WITH_OTHERS]:
    linkedinUrls.connectingWithOthersNotificationUrl,
  [LinkedinSettingKey.POSTING_COMMENTING]:
    linkedinUrls.postingCommentingNotificationUrl,
  [LinkedinSettingKey.MESSAGING]: linkedinUrls.messagingNotificationUrl,
  [LinkedinSettingKey.GROUPS]: linkedinUrls.groupsNotificationUrl,
  [LinkedinSettingKey.PAGES]: linkedinUrls.pagesNotificationUrl,
  [LinkedinSettingKey.EVENTS]: linkedinUrls.eventsNotificationUrl,
  [LinkedinSettingKey.NEWS]: linkedinUrls.newsNotificationUrl,
  [LinkedinSettingKey.REPORTS]: linkedinUrls.reportsNotificationUrl,
  [LinkedinSettingKey.UPDATING_YOUR_PROFILE]:
    linkedinUrls.updatingYourProfileNotificationUrl,
  [LinkedinSettingKey.VERIFICATIONS]: linkedinUrls.verificationsNotificationUrl,
}

const gatherStageDefs: StageDef[] = Object.keys(settingsUrls).map((s) => ({
  type: StageType.ACTION,
  key: `GATHER_LINKDIN_NOTIFICATION_${s}`,
  status: LINKEDIN_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
  url: settingsUrls[s],
  action: async (_machineContext, userContext) => {
    await saveCurrentValueForCheckbox(
      userContext.updateStoredAutomationErrors,
      userContext.updateStoredInitialValues,
      settingsLabels,
      s,
      LinkedinRecommendationKey.LINKEDIN_NOTIFICATION_PREFERENCES,
      'p',
    )
  },
}))

const updateStageDefs: StageDef[] = Object.keys(settingsUrls).map((s) => ({
  type: StageType.ACTION,
  key: `UPDATE_LINKDIN_NOTIFICATION_${s}`,
  status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
  url: settingsUrls[s],
  action: async (_machineContext, userContext) => {
    const { isDry, settingsSelected } = userContext

    const currentSetting = await getCurrentValueForCheckbox(
      settingsLabels[s],
      'p',
    )

    if (settingsSelected[s] && currentSetting && !isDry) {
      await toggleCheckbox(settingsLabels[s], 'p')
    }
  },
}))

const undoStageDefs: StageDef[] = Object.keys(settingsUrls).map((s) => ({
  type: StageType.ACTION,
  key: `UPDATE_LINKDIN_NOTIFICATION_${s}`,
  status: LINKEDIN_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
  url: settingsUrls[s],
  action: async (_machineContext, userContext) => {
    const { initialValues, isDry, settingsSelected } = userContext

    const currentSetting = await getCurrentValueForCheckbox(
      settingsLabels[s],
      'p',
    )

    if (
      settingsSelected[s] &&
      currentSetting !== initialValues?.[s] &&
      !isDry
    ) {
      await toggleCheckbox(settingsLabels[s], 'p')
    }
  },
}))

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
